/**
 * ScheduleTelehealthModal Component
 *
 * Modal for scheduling a new telehealth session with a member
 */

import React, { useState, useEffect } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { format, addMinutes, setHours, setMinutes, startOfDay } from 'date-fns';
import { useFormattedDate } from '../../hooks/useUserTimezone';
import {
  X,
  Video,
  Phone,
  Calendar,
  Clock,
  User,
  AlertCircle,
  Check,
  ChevronDown,
} from 'lucide-react';
import { telehealthSessionAPI } from '@/services/api/telehealth';
import { calendarAPI } from '@/services/api/calendar';
import { useAppTranslation } from '@/contexts/TranslationContext';
import {
  ScheduleTelehealthModalProps,
  TelehealthSession,
  MeetingProvider,
} from './types';

// Duration options in minutes
const DURATION_OPTIONS = [
  { value: 15, label: '15 minutes' },
  { value: 30, label: '30 minutes' },
  { value: 45, label: '45 minutes' },
  { value: 60, label: '1 hour' },
  { value: 90, label: '1.5 hours' },
];

// Meeting provider options
const MEETING_PROVIDERS: { value: MeetingProvider; label: string; icon: React.ReactNode }[] = [
  { value: 'twilio', label: 'Twilio Video (Recommended)', icon: <Video className="h-4 w-4" /> },
  // No 'jitsi' (TRUS-688): the public meet.jit.si room it produced could be joined
  // by anyone with the URL. "twilio" gets a WeaveIn join link the member opens
  // from the app, signed in as themselves.
  { value: 'zoom', label: 'Zoom', icon: <Video className="h-4 w-4" /> },
  { value: 'google_meet', label: 'Google Meet', icon: <Video className="h-4 w-4" /> },
  { value: 'teams', label: 'Microsoft Teams', icon: <Video className="h-4 w-4" /> },
  { value: 'phone', label: 'Phone Call Only', icon: <Phone className="h-4 w-4" /> },
];

/** Current local time rounded up to the next 15-minute slot, as 'HH:mm' —
 *  a sensible schedule default that starts from the user's system time. */
function nextQuarterHour(): string {
  const d = new Date();
  d.setSeconds(0, 0);
  d.setMinutes(Math.ceil((d.getMinutes() + 1) / 15) * 15);
  const hh = String(d.getHours()).padStart(2, '0');
  const mm = String(d.getMinutes()).padStart(2, '0');
  return `${hh}:${mm}`;
}

export default function ScheduleTelehealthModal({
  open,
  onClose,
  memberId,
  memberName,
  defaultDate,
  onScheduled,
}: ScheduleTelehealthModalProps) {
  const queryClient = useQueryClient();
  const { fmt } = useFormattedDate();
  const { t } = useAppTranslation();

  // Form state
  const [selectedDate, setSelectedDate] = useState<string>(
    format(defaultDate || new Date(), 'yyyy-MM-dd')
  );
  const [selectedTime, setSelectedTime] = useState<string>(nextQuarterHour());
  const [duration, setDuration] = useState<number>(30);
  const [meetingProvider, setMeetingProvider] = useState<MeetingProvider>('twilio');
  const [isAudioOnly, setIsAudioOnly] = useState(false);
  const [enableRecording, setEnableRecording] = useState(false);
  const [notes, setNotes] = useState('');
  const [error, setError] = useState<string | null>(null);

  // Reset form when modal opens
  useEffect(() => {
    if (open) {
      setSelectedDate(format(defaultDate || new Date(), 'yyyy-MM-dd'));
      setSelectedTime(nextQuarterHour());
      setDuration(30);
      setMeetingProvider('twilio');
      setIsAudioOnly(false);
      setEnableRecording(false);
      setNotes('');
      setError(null);
    }
  }, [open, defaultDate]);

  // Update audio-only when phone is selected
  useEffect(() => {
    if (meetingProvider === 'phone') {
      setIsAudioOnly(true);
    }
  }, [meetingProvider]);

  // Create calendar event and telehealth session
  const scheduleMutation = useMutation({
    mutationFn: async () => {
      // First, create a calendar event
      const scheduledDateTime = new Date(`${selectedDate}T${selectedTime}`);
      const endDateTime = addMinutes(scheduledDateTime, duration);

      const calendarEvent = await calendarAPI.createEvent({
        title: `Telehealth Session with ${memberName}`,
        description: notes || `Virtual appointment with ${memberName}`,
        start_time: scheduledDateTime.toISOString(),
        end_time: endDateTime.toISOString(),
        event_type: 'appointment',
        location_type: meetingProvider === 'phone' ? 'phone' : 'virtual',
        meeting_provider: meetingProvider === 'phone' ? undefined : meetingProvider,
        member_id: memberId,
        all_day: false,
        create_meeting_link: meetingProvider !== 'phone',
      });

      // Then create the telehealth session linked to the calendar event
      const session = await telehealthSessionAPI.createSession({
        calendar_event_id: calendarEvent.id,
        member_id: memberId,
        scheduled_duration_minutes: duration,
        is_audio_only: isAudioOnly,
        recording_enabled: enableRecording,
      });

      return session;
    },
    onSuccess: (session) => {
      queryClient.invalidateQueries({ queryKey: ['telehealth-upcoming-sessions'] });
      queryClient.invalidateQueries({ queryKey: ['calendar-events'] });
      onScheduled?.(session);
      onClose();
    },
    onError: (err: any) => {
      // Handle validation errors which may be an array of objects
      const detail = err.response?.data?.detail;
      if (Array.isArray(detail)) {
        // Validation errors come as array of {type, loc, msg, input, ctx}
        const messages = detail.map((d: any) => d.msg || String(d)).join(', ');
        setError(messages || 'Validation error');
      } else if (typeof detail === 'object' && detail !== null) {
        // Single validation error object
        setError(detail.msg || JSON.stringify(detail));
      } else {
        setError(detail || err.message || 'Failed to schedule session');
      }
    },
  });

  // Generate time options (every 15 minutes, 24-hour availability)
  const timeOptions = [];
  for (let hour = 0; hour < 24; hour++) {
    for (let minute = 0; minute < 60; minute += 15) {
      const time = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
      const label = format(setMinutes(setHours(new Date(), hour), minute), 'h:mm a');
      timeOptions.push({ value: time, label });
    }
  }

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />

      {/* Modal */}
      <div className="relative bg-white dark:bg-gray-800 rounded-xl shadow-xl w-full max-w-lg mx-4 overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-teal-100 dark:bg-teal-900/30 rounded-lg">
              <Video className="h-5 w-5 text-teal-600 dark:text-teal-400" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                Schedule Telehealth Session
              </h2>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                with {memberName}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-5 max-h-[calc(100vh-200px)] overflow-y-auto">
          {/* Error message */}
          {error && (
            <div className="flex items-center gap-2 p-3 bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300 rounded-lg text-sm">
              <AlertCircle className="h-4 w-4 flex-shrink-0" />
              {error}
            </div>
          )}

          {/* Date and Time */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                <Calendar className="h-4 w-4 inline mr-1.5" />
                Date
              </label>
              <input
                type="date"
                value={selectedDate}
                onChange={(e) => setSelectedDate(e.target.value)}
                min={format(new Date(), 'yyyy-MM-dd')}
                className="w-full px-3 py-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                <Clock className="h-4 w-4 inline mr-1.5" />
                Time
              </label>
              <select
                value={selectedTime}
                onChange={(e) => setSelectedTime(e.target.value)}
                className="w-full px-3 py-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
              >
                {timeOptions.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Duration */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
              Duration
            </label>
            <div className="flex flex-wrap gap-2">
              {DURATION_OPTIONS.map((opt) => (
                <button
                  key={opt.value}
                  onClick={() => setDuration(opt.value)}
                  className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                    duration === opt.value
                      ? 'bg-teal-100 dark:bg-teal-900/30 text-teal-700 dark:text-teal-300 border-2 border-teal-500'
                      : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 border-2 border-transparent hover:bg-gray-200 dark:hover:bg-gray-600'
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          {/* Meeting Provider */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
              Meeting Type
            </label>
            <div className="space-y-2">
              {MEETING_PROVIDERS.map((provider) => (
                <button
                  key={provider.value}
                  onClick={() => setMeetingProvider(provider.value)}
                  className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg border-2 transition-colors text-left ${
                    meetingProvider === provider.value
                      ? 'border-teal-500 bg-teal-50 dark:bg-teal-900/20'
                      : 'border-gray-200 dark:border-gray-600 hover:border-gray-300 dark:hover:border-gray-500'
                  }`}
                >
                  <span
                    className={
                      meetingProvider === provider.value
                        ? 'text-teal-600 dark:text-teal-400'
                        : 'text-gray-400'
                    }
                  >
                    {provider.icon}
                  </span>
                  <span
                    className={`text-sm ${
                      meetingProvider === provider.value
                        ? 'text-teal-700 dark:text-teal-300 font-medium'
                        : 'text-gray-700 dark:text-gray-300'
                    }`}
                  >
                    {provider.label}
                  </span>
                  {meetingProvider === provider.value && (
                    <Check className="h-4 w-4 ml-auto text-teal-600 dark:text-teal-400" />
                  )}
                </button>
              ))}
            </div>
          </div>

          {meetingProvider === 'twilio' && (
            <p className="-mt-2 text-xs text-gray-500 dark:text-gray-400" data-testid="twilio-link-note">
              {t(
                'calendar.visitJoin.twilioLinkNote',
                'A WeaveIn video link is created for this visit. Only the member and your team can join it.'
              )}
            </p>
          )}

          {/* Options */}
          <div className="space-y-3">
            {meetingProvider !== 'phone' && (
              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={isAudioOnly}
                  onChange={(e) => setIsAudioOnly(e.target.checked)}
                  className="h-4 w-4 rounded border-gray-300 text-teal-600 focus:ring-teal-500"
                />
                <span className="text-sm text-gray-700 dark:text-gray-300">
                  Audio only (no video)
                </span>
              </label>
            )}
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={enableRecording}
                onChange={(e) => setEnableRecording(e.target.checked)}
                className="h-4 w-4 rounded border-gray-300 text-teal-600 focus:ring-teal-500"
              />
              <span className="text-sm text-gray-700 dark:text-gray-300">
                Enable session recording (requires consent)
              </span>
            </label>
          </div>

          {/* Notes */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
              Notes (optional)
            </label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
              placeholder="Add any notes about the appointment..."
              className="w-full px-3 py-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 resize-none"
            />
          </div>

          {/* Summary */}
          <div className="bg-gray-50 dark:bg-gray-900/50 rounded-lg p-4">
            <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Session Summary
            </h4>
            <div className="space-y-1 text-sm text-gray-600 dark:text-gray-400">
              <p>
                <User className="h-3.5 w-3.5 inline mr-1.5" />
                {memberName}
              </p>
              <p>
                <Calendar className="h-3.5 w-3.5 inline mr-1.5" />
                {fmt.custom(`${selectedDate}T${selectedTime}`, 'EEEE, MMMM d, yyyy')}
              </p>
              <p>
                <Clock className="h-3.5 w-3.5 inline mr-1.5" />
                {fmt.custom(`${selectedDate}T${selectedTime}`, 'h:mm a')} -{' '}
                {fmt.custom(
                  addMinutes(new Date(`${selectedDate}T${selectedTime}`), duration),
                  'h:mm a'
                )}{' '}
                ({duration} min)
              </p>
              <p>
                {meetingProvider === 'phone' ? (
                  <Phone className="h-3.5 w-3.5 inline mr-1.5" />
                ) : (
                  <Video className="h-3.5 w-3.5 inline mr-1.5" />
                )}
                {MEETING_PROVIDERS.find((p) => p.value === meetingProvider)?.label}
                {isAudioOnly && meetingProvider !== 'phone' && ' (Audio only)'}
              </p>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 bg-gray-50 dark:bg-gray-850 border-t border-gray-200 dark:border-gray-700 flex items-center justify-end gap-3">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={() => scheduleMutation.mutate()}
            disabled={scheduleMutation.isPending}
            className="px-6 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            {scheduleMutation.isPending ? (
              <>
                <div className="h-4 w-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                Scheduling...
              </>
            ) : (
              <>
                <Calendar className="h-4 w-4" />
                Schedule Session
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
